import type * as React from 'react'
import { forwardRef, useEffect, useId, useRef } from 'react'
import { Slot } from '@radix-ui/react-slot'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'

import usePortalContainer from '../../helpers/usePortalContainer'
import { useDeprecationWarning } from '../../hooks/useDeprecationWarning'
import { useTooltipGroup } from './TooltipGroup'
import type { TooltipAlign, TooltipSide } from './TooltipGroup'
import { TooltipShortcut } from './TooltipShortcut'

import styles from './Tooltip.module.css'

type TooltipElement = React.ElementRef<typeof TooltipPrimitive.Content>
type TooltipProps = {
  /**
   * Content displayed inside the tooltip
   */
  content: React.ReactNode
  /**
   * Shortcut for tooltip, to display keyboard shortcut
   * @deprecated Use the `content` prop to include `<Kbd>` components directly instead
   */
  shortcut?: string
  /**
   * Children is the trigger of the tooltip
   */
  children: React.ReactNode
  /**
   * Side the tooltip is displayed on
   * @default 'top'
   */
  side?: TooltipSide
  /**
   * Offset of the tooltip
   * @default 0
   */
  sideOffset?: number
  /**
   * Align the tooltip
   * @default 'center'
   */
  align?: TooltipAlign
  /**
   * Delay in ms before the tooltip is shown
   * @default 300
   */
  delay?: number
  /**
   * Controls whether the arrow is displayed
   * @default false
   */
  arrow?: boolean
  /**
   * Controls whether the tooltip is open.
   *
   * Not supported inside a `TooltipGroup` — the group drives open state from
   * hover/focus so only one member is ever shown.
   */
  open?: boolean
  /**
   * Controls whether the tooltip is open by default
   * @default false
   */
  defaultOpen?: boolean
  /**
   * Callback when the tooltip is opened or closed
   */
  onOpenChange?: (open: boolean) => void
}

const Tooltip = forwardRef<TooltipElement, TooltipProps>(
  (
    {
      content,
      shortcut,
      children,
      side = 'top',
      align = 'center',
      delay = 300,
      arrow = false,
      sideOffset = 0,
      open,
      defaultOpen,
      onOpenChange,
    }: TooltipProps,
    ref,
  ) => {
    const container = usePortalContainer()
    const group = useTooltipGroup()
    const id = useId()
    const elementRef = useRef<HTMLElement | null>(null)

    useDeprecationWarning(
      shortcut !== undefined,
      'Tooltip: The `shortcut` prop is deprecated. Use the `content` prop to include `<Kbd>` components directly instead.',
    )

    // `group` is a new object every time any member's active state changes
    // (see TooltipGroup's useMemo deps), including this trigger's own
    // activation — so the effect below must not depend on `group` directly,
    // or its cleanup fires on every such change, not just true unmount
    // (closing a tooltip immediately after it opens, via its own effect
    // seeing itself become active). Reading the latest value through a ref
    // keeps the cleanup itself tied only to `id`, which is stable for the
    // component's lifetime, so it only ever runs on real unmount.
    const groupRef = useRef(group)
    groupRef.current = group

    // A grouped trigger can unmount while it's the active/pending member
    // (e.g. a toggle button swapping to a different element on click) —
    // without this, the shared bubble is left positioned against a
    // now-detached node with nothing left to close it.
    useEffect(() => {
      return (): void => groupRef.current?.forceHide(id)
    }, [id])

    // Activating this trigger (e.g. a keyboard click toggling its own label)
    // can change `content` without a new hover/focus event ever firing —
    // nothing else re-syncs the bubble to the latest content while this
    // trigger stays the active member.
    useEffect(() => {
      if (group?.activeId !== id) return
      const element = elementRef.current
      if (!element) return
      group.show({
        id,
        element,
        content,
        shortcut,
        side,
        align,
        sideOffset,
        arrow,
      })
    }, [group, id, content, shortcut, side, align, sideOffset, arrow])

    if (group) {
      const isActive = group.activeId === id

      const handleShow = (): void => {
        const element = elementRef.current
        if (!element) return
        group.show({
          id,
          element,
          content,
          shortcut,
          side,
          align,
          sideOffset,
          arrow,
        })
        onOpenChange?.(true)
      }

      const handleHide = (): void => {
        group.scheduleHide(id)
        onOpenChange?.(false)
      }

      return (
        <Slot
          ref={elementRef}
          aria-describedby={isActive ? group.contentId : undefined}
          onPointerEnter={handleShow}
          onPointerLeave={handleHide}
          onFocus={handleShow}
          onBlur={handleHide}
        >
          {children}
        </Slot>
      )
    }

    return (
      <TooltipPrimitive.Root
        delayDuration={delay}
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
      >
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal container={container}>
          <TooltipPrimitive.Content
            ref={ref}
            className={styles.content}
            side={side}
            align={align}
            sideOffset={sideOffset}
            hideWhenDetached
          >
            {content}
            {shortcut && <TooltipShortcut shortcut={shortcut} />}
            {arrow && <TooltipPrimitive.Arrow className={styles.arrow} />}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    )
  },
)
Tooltip.displayName = 'Tooltip'

export { Tooltip }
export type { TooltipProps }
